<script>
	import { onMount } from 'svelte';
	import { fly, slide, fade } from 'svelte/transition';
	import { flowsAPI, agentsAPI, teamAPI, usersAPI, channelsAPI } from './api.js';
	import { toast } from './lib/toast.js';
	import Icon from './lib/Icon.svelte';
	import { NODE_HELP } from './lib/flowTemplates.js';

	let { flowId, onClose } = $props();

	let helpOpen = $state(false);

	async function activateNow() {
		if (!flow) return;
		flow.is_active = true;
		const ok = await save();
		if (!ok) {
			// La validación falló: revierte a borrador para no mentir el estado.
			flow.is_active = false;
		}
	}

	let flow = $state(null);
	let loading = $state(true);
	let saving = $state(false);
	let agents = $state([]);
	let teams = $state([]);
	let orgUsers = $state([]);
	let channels = $state([]);

	let selectedNodeId = $state(null);
	let testInput = $state('');
	let testRunning = $state(false);
	let testOutput = $state(null);
	let validationErrors = $state([]);
	let validationWarnings = $state([]);
	let stats = $state(null);

	const NODE_META = {
		message:   { icon: 'send',         label: 'Enviar mensaje',     color: 'var(--accent)' },
		ask:       { icon: 'help-circle',  label: 'Pregunta',           color: 'var(--info)' },
		menu:      { icon: 'list',         label: 'Menú de opciones',   color: 'var(--accent)' },
		condition: { icon: 'filter',       label: 'Condición',          color: 'var(--warning)' },
		condition_is_new_contact: { icon: 'user',   label: '¿Contacto nuevo?',  color: 'var(--warning)' },
		condition_has_label:      { icon: 'tag',    label: '¿Tiene etiqueta?',  color: 'var(--warning)' },
		business_hours:           { icon: 'clock',  label: '¿Horario de atención?', color: 'var(--warning)' },
		ai_agent:  { icon: 'sparkles',     label: 'AI Agent',           color: 'var(--brand)' },
		handoff:   { icon: 'arrow-right',  label: 'Handoff a humano',   color: 'var(--danger)' },
		tag:       { icon: 'tag',          label: 'Etiquetar contacto', color: 'var(--text-2)' },
		assign:    { icon: 'user',         label: 'Asignar a agente',   color: 'var(--info)' },
		label:     { icon: 'tag',          label: 'Etiquetar conversación', color: 'var(--warning)' },
		status:    { icon: 'check',        label: 'Cambiar estado',     color: 'var(--success)' },
		webhook:   { icon: 'plug',         label: 'Webhook externo',    color: 'var(--text-2)' },
		wait:      { icon: 'circle',       label: 'Esperar',            color: 'var(--text-3)' },
		end:       { icon: 'check-circle', label: 'Fin',                color: 'var(--success)' }
	};

	let selectedNode = $derived(
		flow && selectedNodeId ? flow.nodes[selectedNodeId] : null
	);

	async function load() {
		loading = true;
		try {
			const [f, ags, ts, us, chs] = await Promise.all([
				flowsAPI.get(flowId),
				agentsAPI.list(),
				teamAPI.listTeams(),
				usersAPI.getAll().catch(() => []),
				channelsAPI.getAll().catch(() => [])
			]);
			flow = f;
			agents = ags || [];
			teams = ts || [];
			orgUsers = us || [];
			channels = chs || [];
			ensurePositions();
			if (!selectedNodeId && flow.start_node_id) selectedNodeId = flow.start_node_id;
		} catch (e) {
			toast.error(e.message || 'Error al cargar');
		} finally {
			loading = false;
		}
	}

	async function save() {
		saving = true;
		validationErrors = [];
		validationWarnings = [];
		try {
			// Pre-validate so the user sees the full error list, not just a toast
			const check = await flowsAPI.validate(flow.id, {
				start_node_id: flow.start_node_id,
				nodes: flow.nodes,
				trigger_type: flow.trigger_type,
				trigger_config: flow.trigger_config,
				is_active: flow.is_active
			});
			validationWarnings = check.warnings || [];
			if (!check.valid) {
				validationErrors = check.errors || [];
				toast.error('El flujo tiene errores — revisa el panel');
				return false;
			}
			const updated = await flowsAPI.update(flow.id, {
				name: flow.name,
				description: flow.description,
				is_active: flow.is_active,
				trigger_type: flow.trigger_type,
				trigger_config: flow.trigger_config,
				priority: flow.priority || 0,
				start_node_id: flow.start_node_id,
				nodes: flow.nodes
			});
			validationWarnings = updated.warnings || validationWarnings;
			flow = updated;
			toast.success('Flujo guardado');
			loadStats();
			return true;
		} catch (e) {
			toast.error(e.message || 'No se pudo guardar');
			return false;
		} finally {
			saving = false;
		}
	}

	async function loadStats() {
		try { stats = await flowsAPI.stats(flowId); } catch {}
	}

	function duplicateNode(id) {
		const src = flow.nodes[id];
		if (!src) return;
		const copy = JSON.parse(JSON.stringify(src));
		copy.id = newNodeId();
		copy.next = null;
		if (copy.branches) copy.branches = { yes: null, no: null };
		flow.nodes = { ...flow.nodes, [copy.id]: copy };
		flow = { ...flow };
		selectedNodeId = copy.id;
		toast.success('Nodo duplicado — conéctalo donde lo necesites');
	}

	function newNodeId() {
		return 'n_' + Math.random().toString(36).slice(2, 10);
	}

	function defaultNode(type) {
		const id = newNodeId();
		const cfg = {
			message:   { text: 'Tu mensaje...' },
			ask:       { question: '¿Cuál es tu nombre?', var_name: 'name' },
			menu:      { question: '¿Con cuál sucursal deseas comunicarte?', var_name: 'choice',
			             options: [{ label: 'Opción 1' }, { label: 'Opción 2' }], max_attempts: 2 },
			condition: { var_name: 'name', op: 'contains', value: '' },
			condition_is_new_contact: {},
			condition_has_label: { label_name: '', scope: 'conversation' },
			business_hours: { start: '08:00', end: '17:00', days: [0,1,2,3,4], tz_offset: -6 },
			ai_agent:  { agent_id: agents[0]?.id || null },
			handoff:   { reason: 'flow_handoff' },
			tag:       { tags: ['interesado'], op: 'add' },
			assign:    { user_id: null, team_id: null },
			label:     { labels: ['nuevo'], op: 'add' },
			status:    { status: 'pending' },
			webhook:   { url: '' },
			wait:      { seconds: 2 },
			end:       {}
		}[type] || {};
		const node = { id, type, config: cfg };
		if (type === 'condition' || type === 'condition_is_new_contact' || type === 'condition_has_label' || type === 'business_hours')
			node.branches = { yes: null, no: null };
		else if (type === 'menu') { node.branches = {}; node.next = null; }
		else if (type !== 'end') node.next = null;
		return node;
	}

	function addNode(type, afterId = null, branch = null) {
		const node = defaultNode(type);
		flow.nodes = { ...flow.nodes, [node.id]: node };
		if (afterId && flow.nodes[afterId]) {
			const parent = flow.nodes[afterId];
			if ((parent.type === 'condition' || parent.type === 'condition_is_new_contact' || parent.type === 'condition_has_label' || parent.type === 'business_hours') && branch) {
				parent.branches = { ...parent.branches, [branch]: node.id };
			} else if ('next' in parent && parent.type !== 'menu') {
				node.next = parent.next;
				parent.next = node.id;
			}
			// Place near the parent on the canvas
			const p = parent.pos || { x: 40, y: 40 };
			node.pos = { x: p.x + CARD_W + 90, y: p.y + (Object.keys(flow.nodes).length % 3) * 40 };
		} else if (!flow.start_node_id) {
			flow.start_node_id = node.id;
		}
		ensurePositions();
		flow = { ...flow };
		selectedNodeId = node.id;
	}

	function deleteNode(id) {
		if (!confirm('¿Eliminar este nodo? Sus enlaces quedarán cortados.')) return;
		const ns = { ...flow.nodes };
		// Cut references
		for (const nid of Object.keys(ns)) {
			const n = ns[nid];
			if (n.next === id) n.next = ns[id]?.next || null;
			if (n.branches) {
				if (n.branches.yes === id) n.branches.yes = null;
				if (n.branches.no === id) n.branches.no = null;
			}
		}
		delete ns[id];
		flow.nodes = ns;
		if (flow.start_node_id === id) {
			flow.start_node_id = Object.keys(ns)[0] || null;
		}
		if (selectedNodeId === id) selectedNodeId = flow.start_node_id;
		flow = { ...flow };
	}

	function setNext(nodeId, value, branch = null) {
		const n = flow.nodes[nodeId];
		if (!n) return;
		if (branch) {
			n.branches = { ...n.branches, [branch]: value || null };
		} else {
			n.next = value || null;
		}
		flow = { ...flow };
	}

	function updateConfig(key, value) {
		if (!selectedNode) return;
		selectedNode.config = { ...selectedNode.config, [key]: value };
		flow = { ...flow };
	}

	async function runTest() {
		testRunning = true;
		testOutput = null;
		try {
			const ok = await save();
			if (!ok) return;
			const result = await flowsAPI.execute(flow.id, { vars: { last_user_message: testInput || '' } });
			testOutput = result;
			toast.success(`Ejecución ${result.status}`);
		} catch (e) {
			toast.error(e.message || 'Error en la ejecución');
		} finally {
			testRunning = false;
		}
	}

	onMount(() => { load(); loadStats(); });

	const NODE_TYPES = [
		'message', 'ask', 'menu', 'condition', 'condition_is_new_contact',
		'condition_has_label', 'business_hours', 'ai_agent', 'handoff', 'tag',
		'assign', 'label', 'status', 'webhook', 'wait', 'end'
	];

	// ===== Graph canvas (n8n-lite): positions, drag, edges, ports =====
	const CARD_W = 230;
	const CARD_H = 70;
	let canvasEl = $state(null);
	let dragging = null;          // { id, dx, dy }
	let pendingConn = $state(null); // { from, branch } while connecting
	let connCursor = $state(null);  // { x, y } cursor pos for the preview line

	function nodeOutputs(node) {
		if (!node) return [];
		if (node.type === 'condition') return [{ key: 'yes', label: 'Sí' }, { key: 'no', label: 'No' }];
		if (node.type === 'condition_is_new_contact') return [{ key: 'yes', label: 'Nuevo' }, { key: 'no', label: 'Existente' }];
		if (node.type === 'condition_has_label') return [{ key: 'yes', label: 'Sí' }, { key: 'no', label: 'No' }];
		if (node.type === 'business_hours') return [{ key: 'yes', label: 'Abierto' }, { key: 'no', label: 'Cerrado' }];
		if (node.type === 'menu') {
			const outs = (node.config?.options || []).map((o, i) =>
				({ key: String(i), label: `${i + 1}· ${(o.label || '').slice(0, 14)}` }));
			outs.push({ key: 'other', label: 'Otro' });
			return outs;
		}
		if (node.type === 'end' || node.type === 'handoff') return [];
		return [{ key: null, label: '' }];
	}

	// Single source of truth for vertical port placement so the SVG line, the out
	// port and the in port all land on the exact same y (no more visual offset).
	const PORT_R = 7; // half of the 14px port circle
	function portY(index, total) {
		return (CARD_H * (index + 1)) / (total + 1);
	}

	function ensurePositions() {
		if (!flow?.nodes) return;
		const missing = Object.values(flow.nodes).filter(n => !n.pos);
		if (!missing.length) return;
		// BFS layering from start: column per depth, lane per sibling
		const depth = {};
		const queue = flow.start_node_id ? [[flow.start_node_id, 0]] : [];
		while (queue.length) {
			const [id, d] = queue.shift();
			if (!(id in flow.nodes) || id in depth) continue;
			depth[id] = d;
			const n = flow.nodes[id];
			Object.values(n.branches || {}).forEach(t => t && queue.push([t, d + 1]));
			if (n.next) queue.push([n.next, d + 1]);
		}
		const lanes = {};
		for (const n of Object.values(flow.nodes)) {
			if (n.pos) continue;
			const d = depth[n.id] ?? 0;
			lanes[d] = (lanes[d] || 0);
			n.pos = { x: 40 + d * (CARD_W + 90), y: 40 + lanes[d] * (CARD_H + 50) };
			lanes[d] += 1;
		}
		flow = { ...flow };
	}

	function canvasPoint(e) {
		const rect = canvasEl.getBoundingClientRect();
		return {
			x: e.clientX - rect.left + canvasEl.scrollLeft,
			y: e.clientY - rect.top + canvasEl.scrollTop
		};
	}

	function startDrag(e, id) {
		if (pendingConn) return;
		const p = canvasPoint(e);
		const node = flow.nodes[id];
		dragging = { id, dx: p.x - (node.pos?.x || 0), dy: p.y - (node.pos?.y || 0) };
		selectedNodeId = id;
	}
	function onCanvasMove(e) {
		// While connecting, track the cursor so we can draw the preview line.
		if (pendingConn) { connCursor = canvasPoint(e); return; }
		if (!dragging) return;
		const p = canvasPoint(e);
		const node = flow.nodes[dragging.id];
		node.pos = { x: Math.max(0, p.x - dragging.dx), y: Math.max(0, p.y - dragging.dy) };
		flow = { ...flow };
	}
	function endDrag() { dragging = null; }

	function startConnection(e, nodeId, branch) {
		e.stopPropagation();
		pendingConn = { from: nodeId, branch };
		connCursor = canvasPoint(e);
	}
	function completeConnection(targetId) {
		connCursor = null;
		if (!pendingConn || pendingConn.from === targetId) { pendingConn = null; return; }
		setNext(pendingConn.from, targetId, pendingConn.branch);
		pendingConn = null;
	}
	function nodeClick(e, id) {
		e.stopPropagation();
		if (pendingConn) { completeConnection(id); return; }
		selectedNodeId = id;
	}

	let edges = $derived.by(() => {
		if (!flow?.nodes) return [];
		const out = [];
		for (const n of Object.values(flow.nodes)) {
			if (!n.pos) continue;
			const outs = nodeOutputs(n);
			outs.forEach((o, idx) => {
				const target = o.key === null ? n.next : (n.branches || {})[o.key];
				const t = target ? flow.nodes[target] : null;
				if (!t || !t.pos) return;
				const x1 = n.pos.x + CARD_W;
				const y1 = n.pos.y + portY(idx, outs.length);
				const x2 = t.pos.x;
				const y2 = t.pos.y + CARD_H / 2;
				const mx = Math.max(40, (x2 - x1) / 2);
				out.push({
					id: `${n.id}-${o.key}-${target}`,
					d: `M ${x1} ${y1} C ${x1 + mx} ${y1}, ${x2 - mx} ${y2}, ${x2} ${y2}`,
					label: o.label,
					lx: (x1 + x2) / 2, ly: (y1 + y2) / 2 - 7
				});
			});
		}
		return out;
	});

	// Live preview line from the chosen out-port to the cursor while connecting.
	let previewEdge = $derived.by(() => {
		if (!pendingConn || !connCursor) return null;
		const n = flow?.nodes?.[pendingConn.from];
		if (!n || !n.pos) return null;
		const outs = nodeOutputs(n);
		const idx = Math.max(0, outs.findIndex(o => o.key === pendingConn.branch));
		const x1 = n.pos.x + CARD_W;
		const y1 = n.pos.y + portY(idx, outs.length);
		const x2 = connCursor.x;
		const y2 = connCursor.y;
		const mx = Math.max(40, (x2 - x1) / 2);
		return `M ${x1} ${y1} C ${x1 + mx} ${y1}, ${x2 - mx} ${y2}, ${x2} ${y2}`;
	});

	let canvasSize = $derived.by(() => {
		let w = 900, h = 600;
		for (const n of Object.values(flow?.nodes || {})) {
			if (!n.pos) continue;
			w = Math.max(w, n.pos.x + CARD_W + 120);
			h = Math.max(h, n.pos.y + CARD_H + 120);
		}
		return { w, h };
	});

	function autoLayout() {
		Object.values(flow.nodes || {}).forEach(n => { delete n.pos; });
		ensurePositions();
	}
</script>

<div class="editor">
	<header class="head">
		<button class="back-btn" onclick={onClose} aria-label="Volver">
			<Icon name="arrow-left" size={16} />
		</button>
		{#if flow}
			<input
				class="name-input"
				bind:value={flow.name}
				placeholder="Nombre del flujo..."
			/>
			<label class="active-toggle">
				<input type="checkbox" bind:checked={flow.is_active} />
				<span>{flow.is_active ? 'Activo' : 'Borrador'}</span>
			</label>
		{/if}
		<button class="btn btn-ghost btn-sm" onclick={() => helpOpen = true}>
			<Icon name="help-circle" size={14} />
			¿Cómo funciona?
		</button>
		<button class="btn btn-ghost btn-sm" onclick={onClose}>Cerrar</button>
		<button class="btn btn-primary btn-sm" onclick={save} disabled={saving || loading}>
			<Icon name="check" size={14} />
			{saving ? 'Guardando...' : 'Guardar'}
		</button>
	</header>

	{#if flow && !flow.is_active}
		<div class="draft-banner" transition:slide={{ duration: 200 }}>
			<Icon name="alert-circle" size={18} />
			<div class="draft-text">
				<strong>Este flujo está en BORRADOR.</strong>
				Lo puedes editar y probar aquí, pero <u>no se ejecuta automáticamente</u> con tus
				contactos hasta que lo actives.
			</div>
			<button class="btn btn-sm btn-primary" onclick={activateNow} disabled={saving}>
				<Icon name="check" size={13} /> Activar ahora
			</button>
		</div>
	{/if}

	{#if loading}
		<div class="loading"><div class="spinner"></div></div>
	{:else if flow}
		<div class="body">
			<!-- Palette -->
			<aside class="palette">
				<div class="palette-title">Trigger</div>
				<div class="trigger-block">
					<select bind:value={flow.trigger_type}>
						<option value="new_conversation">Conversación nueva</option>
						<option value="message_in">Mensaje entrante</option>
						<option value="keyword">Palabra clave</option>
						<option value="manual">Manual (solo API / no auto-dispara)</option>
					</select>
					<p class="trigger-help">
						{#if flow.trigger_type === 'new_conversation'}
							Se dispara solo con el <strong>primer mensaje</strong> de una conversación.
						{:else if flow.trigger_type === 'message_in'}
							Se dispara con <strong>cada mensaje entrante</strong> (si no hay otro flujo en curso).
						{:else if flow.trigger_type === 'keyword'}
							Se dispara cuando el mensaje coincide con una <strong>palabra clave</strong>.
						{:else}
							<strong>No se dispara solo.</strong> Solo corre por API o con el botón de prueba.
						{/if}
					</p>
					{#if flow.trigger_type === 'keyword'}
						<input
							type="text"
							placeholder="hola, ayuda, precio..."
							value={(flow.trigger_config?.keywords || []).join(', ')}
							oninput={(e) => {
								flow.trigger_config = {
									...(flow.trigger_config || {}),
									keywords: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
								};
							}}
						/>
						<select
							value={flow.trigger_config?.match || 'any'}
							onchange={(e) => {
								flow.trigger_config = { ...(flow.trigger_config || {}), match: e.target.value };
							}}
						>
							<option value="any">Contiene la palabra</option>
							<option value="word">Palabra completa</option>
							<option value="exact">Mensaje exacto</option>
						</select>
					{/if}
					{#if flow.trigger_type !== 'manual'}
						<label class="trigger-label">Canales (vacío = todos)</label>
						<div class="channel-picker">
							{#each channels as ch}
								<label class="channel-opt">
									<input
										type="checkbox"
										checked={(flow.trigger_config?.channels || []).includes(ch.id)}
										onchange={(e) => {
											const cur = new Set(flow.trigger_config?.channels || []);
											if (e.target.checked) cur.add(ch.id); else cur.delete(ch.id);
											flow.trigger_config = { ...(flow.trigger_config || {}), channels: [...cur] };
										}}
									/>
									{ch.name}
								</label>
							{/each}
							{#if !channels.length}<span class="channel-empty">No hay canales.</span>{/if}
						</div>
						<label class="trigger-label" title="Si varios flujos activos coinciden, gana el de mayor prioridad">
							Prioridad
							<input
								type="number"
								class="priority-input"
								value={flow.priority || 0}
								oninput={(e) => { flow.priority = parseInt(e.target.value) || 0; }}
							/>
						</label>
					{/if}
					<input
						type="text"
						placeholder="Salida: salir, cancelar"
						title="Si el cliente escribe una de estas palabras, el flujo se detiene"
						value={(flow.trigger_config?.exit_keywords || []).join(', ')}
						oninput={(e) => {
							flow.trigger_config = {
								...(flow.trigger_config || {}),
								exit_keywords: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
							};
						}}
					/>
					<input
						type="text"
						placeholder="Menú: menu, menú, inicio"
						title="Si el cliente escribe una de estas palabras, vuelve al menú principal. Por defecto: menu, menú, inicio"
						value={(flow.trigger_config?.return_keywords || []).join(', ')}
						oninput={(e) => {
							flow.trigger_config = {
								...(flow.trigger_config || {}),
								return_keywords: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
							};
						}}
					/>
				</div>

				{#if validationErrors.length || validationWarnings.length}
					<div class="palette-title">Problemas</div>
					<div class="issues">
						{#each validationErrors as err}
							<div class="issue error"><Icon name="alert-circle" size={11} /> {err}</div>
						{/each}
						{#each validationWarnings as warn}
							<div class="issue warn"><Icon name="alert-circle" size={11} /> {warn}</div>
						{/each}
					</div>
				{/if}

				<div class="palette-title">Añadir nodo</div>
				<div class="palette-list">
					{#each NODE_TYPES as t}
						{@const meta = NODE_META[t]}
						<button class="palette-item" onclick={() => addNode(t, selectedNodeId)}>
							<span class="pi-icon" style="background:{meta.color}; color:#fff">
								<Icon name={meta.icon} size={11} />
							</span>
							<span>{meta.label}</span>
							<Icon name="plus" size={11} />
						</button>
					{/each}
				</div>

				<div class="palette-title">Probar</div>
				<div class="test-block">
					<input
						type="text"
						placeholder="Mensaje de prueba"
						bind:value={testInput}
					/>
					<button class="btn btn-secondary btn-sm" onclick={runTest} disabled={testRunning}>
						<Icon name="zap" size={13} />
						{testRunning ? 'Ejecutando...' : 'Ejecutar'}
					</button>
					{#if testOutput}
						<div class="test-result {testOutput.status}">
							<div><b>{testOutput.status}</b></div>
							{#if testOutput.error}<div class="err">{testOutput.error}</div>{/if}
							{#if testOutput.log?.length}
								<div class="log-list">
									{#each testOutput.log as l}
										<div class="log-row">→ {l.type || ''}{l.ai_error ? ` ⚠ ${l.ai_error}` : ''} <span class="log-id">{l.node_id || ''}</span></div>
									{/each}
								</div>
							{/if}
						</div>
					{/if}
				</div>

				{#if stats && stats.total_executions > 0}
					<div class="palette-title">Estadísticas</div>
					<div class="stats-block">
						<div class="stat-row"><span>Ejecuciones</span><b>{stats.total_executions}</b></div>
						{#if stats.completion_rate !== null}
							<div class="stat-row"><span>Completadas</span><b>{stats.completion_rate}%</b></div>
						{/if}
						{#if stats.avg_duration_seconds !== null}
							<div class="stat-row"><span>Duración media</span><b>{stats.avg_duration_seconds}s</b></div>
						{/if}
						{#if stats.drop_off?.length}
							<div class="stat-subtitle">Dónde se quedan:</div>
							{#each stats.drop_off.slice(0, 3) as d}
								<button class="stat-drop" onclick={() => (selectedNodeId = d.node_id)}>
									{d.count}× — {d.label}
								</button>
							{/each}
						{/if}
					</div>
				{/if}
			</aside>

			<!-- Canvas (graph view: draggable nodes + SVG edges) -->
			<main
				class="canvas graph"
				bind:this={canvasEl}
				onpointermove={onCanvasMove}
				onpointerup={endDrag}
				onpointerleave={endDrag}
				onclick={() => { pendingConn = null; connCursor = null; }}
			>
				{#if !Object.keys(flow.nodes || {}).length}
					<div class="canvas-empty">
						<Icon name="zap" size={32} />
						<p>Empieza añadiendo un nodo desde la izquierda</p>
					</div>
				{:else}
					<div class="graph-toolbar">
						<button class="btn btn-secondary btn-sm" onclick={(e) => { e.stopPropagation(); autoLayout(); }}>
							<Icon name="kanban" size={13} /> Ordenar
						</button>
						{#if pendingConn}
							<span class="conn-hint">Haz clic en el nodo destino… (clic en el fondo cancela)</span>
						{/if}
					</div>
					<div class="graph-area" style="width:{canvasSize.w}px; height:{canvasSize.h}px">
						<svg class="edges" width={canvasSize.w} height={canvasSize.h}>
							<defs>
								<marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
									<path d="M 0 0 L 10 5 L 0 10 z" fill="var(--border-2)" />
								</marker>
							</defs>
							{#each edges as edge (edge.id)}
								<path d={edge.d} fill="none" stroke="var(--border-2)" stroke-width="2" marker-end="url(#arrow)" />
								{#if edge.label}
									<text x={edge.lx} y={edge.ly} text-anchor="middle" class="edge-label">{edge.label}</text>
								{/if}
							{/each}
							{#if previewEdge}
								<path d={previewEdge} fill="none" stroke="var(--accent)" stroke-width="2" stroke-dasharray="6 4" marker-end="url(#arrow)" />
							{/if}
						</svg>

						{#each Object.values(flow.nodes) as node (node.id)}
							{@const meta = NODE_META[node.type] || NODE_META.message}
							{@const outs = nodeOutputs(node)}
							<div
								class="gnode"
								class:selected={selectedNodeId === node.id}
								class:start={flow.start_node_id === node.id}
								class:conn-target={pendingConn && pendingConn.from !== node.id}
								style="left:{node.pos?.x || 0}px; top:{node.pos?.y || 0}px; width:{CARD_W}px"
								onpointerdown={(e) => startDrag(e, node.id)}
								onclick={(e) => nodeClick(e, node.id)}
								onkeydown={(e) => { if (e.key === 'Enter') { e.preventDefault(); nodeClick(e, node.id); } }}
								role="button"
								tabindex="0"
							>
								{#if flow.start_node_id === node.id}
									<span class="start-pill"><Icon name="zap" size={9} /> Inicio</span>
								{/if}
								<div class="gnode-row">
									<div class="nc-icon" style="background:{meta.color}; color:#fff">
										<Icon name={meta.icon} size={12} />
									</div>
									<div class="nc-text">
										<div class="nc-type">{meta.label}</div>
										<div class="nc-preview">
											{#if node.type === 'message'}{node.config?.text?.slice(0, 46) || '(vacío)'}
											{:else if node.type === 'ask'}{node.config?.question?.slice(0, 46) || '(vacío)'}
											{:else if node.type === 'menu'}{node.config?.question?.slice(0, 32) || '(vacío)'} · {(node.config?.options || []).length} opciones
											{:else if node.type === 'condition'}{`{{${node.config?.var_name}}}`} {node.config?.op} "{node.config?.value}"
											{:else if node.type === 'condition_is_new_contact'}Nuevo vs. existente
											{:else if node.type === 'condition_has_label'}etiqueta: "{node.config?.label_name || '(sin definir)'}"
											{:else if node.type === 'ai_agent'}{agents.find(a => a.id === node.config?.agent_id)?.name || '(no asignado)'}
											{:else if node.type === 'handoff'}{node.config?.team_id ? `Equipo: ${teams.find(t => t.id === node.config.team_id)?.name}` : 'A cualquier humano'}
											{:else if node.type === 'tag'}{(node.config?.tags || []).join(', ')}
											{:else if node.type === 'assign'}{orgUsers.find(u => u.id === node.config?.user_id)?.name || teams.find(t => t.id === node.config?.team_id)?.name || '(sin destino)'}
											{:else if node.type === 'label'}{(node.config?.labels || []).join(', ')}
											{:else if node.type === 'status'}→ {node.config?.status}
											{:else if node.type === 'webhook'}{(node.config?.url || '(sin URL)').slice(0, 38)}
											{:else if node.type === 'wait'}{node.config?.seconds}s
											{:else if node.type === 'end'}Termina aquí
											{/if}
										</div>
									</div>
									<button class="del-btn" onpointerdown={(e) => e.stopPropagation()} onclick={(e) => { e.stopPropagation(); deleteNode(node.id); }} title="Eliminar" aria-label="Eliminar nodo">
										<Icon name="trash" size={11} />
									</button>
								</div>

								{#each outs as out, i (out.key ?? 'next')}
									<button
										class="port out"
										class:active={pendingConn?.from === node.id && pendingConn?.branch === out.key}
										style="top:{portY(i, outs.length) - PORT_R}px"
										title={out.label ? `Conectar: ${out.label}` : 'Conectar siguiente'}
										onpointerdown={(e) => e.stopPropagation()}
										onclick={(e) => startConnection(e, node.id, out.key)}
										aria-label="Conectar"
									></button>
								{/each}
								{#if pendingConn && pendingConn.from !== node.id}
									<span class="port in" style="top:{CARD_H / 2 - PORT_R}px"></span>
								{/if}
							</div>
						{/each}
					</div>
				{/if}
			</main>

			<!-- Inspector -->
			<aside class="inspector">
				{#if !selectedNode}
					<div class="inspector-empty">Selecciona un nodo</div>
				{:else}
					{@const meta = NODE_META[selectedNode.type]}
					<div class="insp-head">
						<div class="nc-icon" style="background:{meta.color}; color:#fff">
							<Icon name={meta.icon} size={14} />
						</div>
						<div style="flex:1; min-width:0">
							<h4>{meta.label}</h4>
							<code class="node-id">{selectedNode.id}</code>
						</div>
						<button class="icon-mini" title="Duplicar nodo" onclick={() => duplicateNode(selectedNode.id)}>
							<Icon name="plus" size={13} />
						</button>
					</div>

					{#if selectedNode.type === 'message'}
						<label>Texto
							<textarea
								rows="4"
								value={selectedNode.config?.text || ''}
								oninput={(e) => updateConfig('text', e.target.value)}
							></textarea>
							<small>Soporta variables: {`{{nombre}}, {{contact.name}}`}</small>
						</label>
					{:else if selectedNode.type === 'ask'}
						<label>Pregunta
							<textarea rows="3"
								value={selectedNode.config?.question || ''}
								oninput={(e) => updateConfig('question', e.target.value)}
							></textarea>
						</label>
						<label>Variable destino
							<input type="text"
								value={selectedNode.config?.var_name || ''}
								oninput={(e) => updateConfig('var_name', e.target.value)}
							/>
						</label>
					{:else if selectedNode.type === 'menu'}
						<label>Pregunta
							<textarea rows="2"
								value={selectedNode.config?.question || ''}
								oninput={(e) => updateConfig('question', e.target.value)}
							></textarea>
							<small>Se envía con las opciones numeradas; el cliente responde con el número o el nombre.</small>
						</label>
						<div class="menu-options">
							<label>Opciones</label>
							{#each (selectedNode.config?.options || []) as opt, i}
								<div class="menu-opt-row">
									<span class="opt-num">{i + 1}</span>
									<input type="text" value={opt.label || ''}
										oninput={(e) => {
											const options = [...(selectedNode.config?.options || [])];
											options[i] = { ...options[i], label: e.target.value };
											updateConfig('options', options);
										}} />
									<select
										value={selectedNode.branches?.[String(i)] || ''}
										onchange={(e) => setNext(selectedNode.id, e.target.value || null, String(i))}
										title="A dónde va esta opción"
									>
										<option value="">— Fin —</option>
										{#each Object.values(flow.nodes).filter(n => n.id !== selectedNode.id) as n}
											<option value={n.id}>{NODE_META[n.type]?.label}: {(n.config?.text || n.config?.question || n.id).slice(0, 24)}</option>
										{/each}
									</select>
									<button class="icon-mini" title="Quitar opción" onclick={() => {
										const options = (selectedNode.config?.options || []).filter((_, j) => j !== i);
										const branches = {};
										options.forEach((_, j) => {
											const old = j >= i ? j + 1 : j;
											if (selectedNode.branches?.[String(old)]) branches[String(j)] = selectedNode.branches[String(old)];
										});
										if (selectedNode.branches?.other) branches.other = selectedNode.branches.other;
										selectedNode.branches = branches;
										updateConfig('options', options);
									}}>
										<Icon name="x" size={12} />
									</button>
								</div>
							{/each}
							<button class="btn btn-secondary btn-sm" onclick={() => {
								updateConfig('options', [...(selectedNode.config?.options || []), { label: '' }]);
							}}>
								<Icon name="plus" size={12} /> Agregar opción
							</button>
						</div>
						<label>Si no elige nada válido →
							<select
								value={selectedNode.branches?.other || ''}
								onchange={(e) => setNext(selectedNode.id, e.target.value || null, 'other')}
							>
								<option value="">— Continuar / Fin —</option>
								{#each Object.values(flow.nodes).filter(n => n.id !== selectedNode.id) as n}
									<option value={n.id}>{NODE_META[n.type]?.label}: {(n.config?.text || n.config?.question || n.id).slice(0, 24)}</option>
								{/each}
							</select>
							<small>Tras {selectedNode.config?.max_attempts || 2} intentos sin respuesta válida.</small>
						</label>
						<label class="checkbox-label">
							<input type="checkbox"
								checked={selectedNode.config?.on_giveup === 'handoff'}
								onchange={(e) => updateConfig('on_giveup', e.target.checked ? 'handoff' : null)}
							/>
							Pasar a un agente humano si no logra elegir
						</label>
						{#if selectedNode.config?.on_giveup === 'handoff'}
							<label>Asignar a equipo (opcional)
								<select
									value={selectedNode.config?.handoff_team_id || ''}
									onchange={(e) => updateConfig('handoff_team_id', e.target.value ? parseInt(e.target.value) : null)}
								>
									<option value="">— Cualquiera —</option>
									{#each teams as t}<option value={t.id}>{t.name}</option>{/each}
								</select>
							</label>
						{/if}
						<label>Variable destino
							<input type="text"
								value={selectedNode.config?.var_name || 'choice'}
								oninput={(e) => updateConfig('var_name', e.target.value)}
							/>
						</label>
						<div class="inactivity-group">
							<div class="inactivity-title">Inactividad (reaparecer menú)</div>
							<div class="inactivity-row">
								<input type="number" min="0" placeholder="0"
									title="0 = desactivado"
									value={selectedNode.config?.inactivity?.value ?? ''}
									oninput={(e) => updateConfig('inactivity', {
										...(selectedNode.config?.inactivity || {}),
										value: parseInt(e.target.value) || 0,
										unit: selectedNode.config?.inactivity?.unit || 'min'
									})}
								/>
								<select
									value={selectedNode.config?.inactivity?.unit || 'min'}
									onchange={(e) => updateConfig('inactivity', {
										...(selectedNode.config?.inactivity || {}),
										value: selectedNode.config?.inactivity?.value || 0,
										unit: e.target.value
									})}
								>
									<option value="min">minutos</option>
									<option value="hours">horas</option>
									<option value="days">días</option>
								</select>
							</div>
							<label>Si no responde, volver a →
								<select
									value={selectedNode.config?.on_timeout_node || ''}
									onchange={(e) => updateConfig('on_timeout_node', e.target.value || null)}
								>
									<option value="">— Inicio del flujo —</option>
									{#each Object.values(flow.nodes) as n}
										<option value={n.id}>{NODE_META[n.type]?.label}: {(n.config?.text || n.config?.question || n.id).slice(0, 24)}</option>
									{/each}
								</select>
							</label>
							<small>Si el cliente no responde en ese tiempo, el bot vuelve a mostrar el menú (o el nodo elegido). 0 = desactivado.</small>
						</div>
					{:else if selectedNode.type === 'condition'}
						<label>Variable
							<input type="text"
								value={selectedNode.config?.var_name || ''}
								oninput={(e) => updateConfig('var_name', e.target.value)}
							/>
						</label>
						<label>Operador
							<select
								value={selectedNode.config?.op || 'equals'}
								onchange={(e) => updateConfig('op', e.target.value)}
							>
								<option value="contains">contiene</option>
								<option value="not_contains">no contiene</option>
								<option value="equals">igual (ignora mayúsculas)</option>
								<option value="equals_cs">igual (estricto)</option>
								<option value="exists">existe</option>
							</select>
						</label>
						{#if selectedNode.config?.op !== 'exists'}
							<label>Valor
								<input type="text"
									value={selectedNode.config?.value || ''}
									oninput={(e) => updateConfig('value', e.target.value)}
								/>
							</label>
						{/if}
					{:else if selectedNode.type === 'condition_is_new_contact'}
						<p class="cfg-hint">Ramifica según si el contacto se acaba de crear (Nuevo) o ya existía (Existente). Conecta ambas salidas.</p>
					{:else if selectedNode.type === 'condition_has_label'}
						<label>Nombre de la etiqueta
							<input type="text"
								value={selectedNode.config?.label_name || ''}
								oninput={(e) => updateConfig('label_name', e.target.value)}
								placeholder="VIP"
							/>
						</label>
						<label>Buscar etiqueta en
							<select
								value={selectedNode.config?.scope || 'conversation'}
								onchange={(e) => updateConfig('scope', e.target.value)}
							>
								<option value="conversation">la conversación</option>
								<option value="contact">el contacto</option>
							</select>
						</label>
					{:else if selectedNode.type === 'business_hours'}
						<p class="cfg-hint">Ramifica según si el cliente escribe dentro o fuera del horario de atención. Conecta ambas salidas (Abierto / Cerrado).</p>
						<div class="bh-row">
							<label>Desde
								<input type="time"
									value={selectedNode.config?.start || '08:00'}
									oninput={(e) => updateConfig('start', e.target.value)}
								/>
							</label>
							<label>Hasta
								<input type="time"
									value={selectedNode.config?.end || '17:00'}
									oninput={(e) => updateConfig('end', e.target.value)}
								/>
							</label>
						</div>
						<label>Días
							<div class="bh-days">
								{#each [['L',0],['M',1],['X',2],['J',3],['V',4],['S',5],['D',6]] as [lbl, d]}
									<button type="button"
										class="bh-day {(selectedNode.config?.days || []).includes(d) ? 'on' : ''}"
										onclick={() => {
											const cur = selectedNode.config?.days || [];
											updateConfig('days', cur.includes(d) ? cur.filter(x => x !== d) : [...cur, d].sort());
										}}
									>{lbl}</button>
								{/each}
							</div>
						</label>
						<label>Zona horaria (offset UTC)
							<input type="number" min="-12" max="14"
								value={selectedNode.config?.tz_offset ?? -6}
								oninput={(e) => updateConfig('tz_offset', parseInt(e.target.value) || 0)}
							/>
							<small>Costa Rica = -6. Las horas se evalúan en esta zona.</small>
						</label>
					{:else if selectedNode.type === 'ai_agent'}
						<label>Agente IA
							<select
								value={selectedNode.config?.agent_id || ''}
								onchange={(e) => updateConfig('agent_id', e.target.value ? parseInt(e.target.value) : null)}
							>
								<option value="">— Selecciona —</option>
								{#each agents as a}<option value={a.id}>{a.name}</option>{/each}
							</select>
						</label>
						<label>Si la IA falla, responder
							<input type="text"
								value={selectedNode.config?.fallback_text || ''}
								oninput={(e) => updateConfig('fallback_text', e.target.value)}
								placeholder="Un asesor te responderá pronto 🙂"
							/>
							<small>Se envía si la IA da error o no hay créditos; el flujo continúa.</small>
						</label>
					{:else if selectedNode.type === 'handoff'}
						<label>Equipo
							<select
								value={selectedNode.config?.team_id || ''}
								onchange={(e) => updateConfig('team_id', e.target.value ? parseInt(e.target.value) : null)}
							>
								<option value="">Cualquiera</option>
								{#each teams as t}<option value={t.id}>{t.name}</option>{/each}
							</select>
						</label>
						<label>Motivo
							<input type="text"
								value={selectedNode.config?.reason || ''}
								oninput={(e) => updateConfig('reason', e.target.value)}
							/>
						</label>
					{:else if selectedNode.type === 'tag'}
						<label>Tags (separados por coma)
							<input type="text"
								value={(selectedNode.config?.tags || []).join(', ')}
								oninput={(e) => updateConfig('tags', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
							/>
						</label>
						<label>Operación
							<select
								value={selectedNode.config?.op || 'add'}
								onchange={(e) => updateConfig('op', e.target.value)}
							>
								<option value="add">Agregar</option>
								<option value="remove">Quitar</option>
								<option value="set">Reemplazar</option>
							</select>
						</label>
					{:else if selectedNode.type === 'assign'}
						<label>Agente
							<select
								value={selectedNode.config?.user_id || ''}
								onchange={(e) => updateConfig('user_id', e.target.value ? parseInt(e.target.value) : null)}
							>
								<option value="">— Ninguno —</option>
								{#each orgUsers as u}<option value={u.id}>{u.name}</option>{/each}
							</select>
						</label>
						<label>Equipo
							<select
								value={selectedNode.config?.team_id || ''}
								onchange={(e) => updateConfig('team_id', e.target.value ? parseInt(e.target.value) : null)}
							>
								<option value="">— Ninguno —</option>
								{#each teams as t}<option value={t.id}>{t.name}</option>{/each}
							</select>
						</label>
					{:else if selectedNode.type === 'label'}
						<label>Etiquetas (separadas por coma)
							<input type="text"
								value={(selectedNode.config?.labels || []).join(', ')}
								oninput={(e) => updateConfig('labels', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
							/>
							<small>Se crean automáticamente si no existen.</small>
						</label>
						<label>Operación
							<select
								value={selectedNode.config?.op || 'add'}
								onchange={(e) => updateConfig('op', e.target.value)}
							>
								<option value="add">Agregar</option>
								<option value="remove">Quitar</option>
							</select>
						</label>
					{:else if selectedNode.type === 'status'}
						<label>Nuevo estado
							<select
								value={selectedNode.config?.status || 'pending'}
								onchange={(e) => updateConfig('status', e.target.value)}
							>
								<option value="open">Abierta</option>
								<option value="pending">Pendiente</option>
								<option value="resolved">Resuelta</option>
								<option value="closed">Cerrada</option>
							</select>
						</label>
					{:else if selectedNode.type === 'webhook'}
						<label>URL destino
							<input type="url"
								value={selectedNode.config?.url || ''}
								oninput={(e) => updateConfig('url', e.target.value)}
								placeholder="https://hooks.zapier.com/..."
							/>
							<small>Recibe un POST JSON con la conversación, el contacto y las variables del flujo.</small>
						</label>
					{:else if selectedNode.type === 'wait'}
						<label>Segundos
							<input type="number" min="0" max="86400"
								value={selectedNode.config?.seconds || 0}
								oninput={(e) => updateConfig('seconds', parseInt(e.target.value) || 0)}
							/>
							<small>Hasta 5s: pausa inmediata. Más de 5s: el flujo se reanuda solo (revisado cada ~30s). Máx 24h.</small>
						</label>
					{/if}

					<!-- Connections -->
					{#if selectedNode.type === 'condition' || selectedNode.type === 'condition_is_new_contact' || selectedNode.type === 'condition_has_label' || selectedNode.type === 'business_hours'}
						<div class="conn-section">
							<label>{selectedNode.type === 'business_hours' ? 'Abierto →' : selectedNode.type === 'condition_is_new_contact' ? 'Nuevo →' : 'Si SÍ →'}
								<select
									value={selectedNode.branches?.yes || ''}
									onchange={(e) => setNext(selectedNode.id, e.target.value || null, 'yes')}
								>
									<option value="">— Fin —</option>
									{#each Object.values(flow.nodes).filter(n => n.id !== selectedNode.id) as n}
										<option value={n.id}>{NODE_META[n.type]?.label}: {(n.config?.text || n.config?.question || n.id).slice(0, 30)}</option>
									{/each}
								</select>
							</label>
							<label>{selectedNode.type === 'business_hours' ? 'Cerrado →' : selectedNode.type === 'condition_is_new_contact' ? 'Existente →' : 'Si NO →'}
								<select
									value={selectedNode.branches?.no || ''}
									onchange={(e) => setNext(selectedNode.id, e.target.value || null, 'no')}
								>
									<option value="">— Fin —</option>
									{#each Object.values(flow.nodes).filter(n => n.id !== selectedNode.id) as n}
										<option value={n.id}>{NODE_META[n.type]?.label}: {(n.config?.text || n.config?.question || n.id).slice(0, 30)}</option>
									{/each}
								</select>
							</label>
						</div>
					{:else if selectedNode.type !== 'end' && selectedNode.type !== 'menu'}
						<div class="conn-section">
							<label>Siguiente nodo →
								<select
									value={selectedNode.next || ''}
									onchange={(e) => setNext(selectedNode.id, e.target.value || null)}
								>
									<option value="">— Fin —</option>
									{#each Object.values(flow.nodes).filter(n => n.id !== selectedNode.id) as n}
										<option value={n.id}>{NODE_META[n.type]?.label}: {(n.config?.text || n.config?.question || n.id).slice(0, 30)}</option>
									{/each}
								</select>
							</label>
						</div>
					{/if}

					<div class="insp-foot">
						<button
							class="btn btn-ghost btn-sm"
							disabled={flow.start_node_id === selectedNode.id}
							onclick={() => { flow.start_node_id = selectedNode.id; flow = {...flow}; toast.success('Marcado como inicio'); }}
						>
							<Icon name="circle" size={12} /> Marcar como inicio
						</button>
					</div>
				{/if}
			</aside>
		</div>
	{/if}

	{#if helpOpen}
		<button class="help-scrim" onclick={() => helpOpen = false} aria-label="Cerrar ayuda" transition:fade={{ duration: 150 }}></button>
		<aside class="help-panel" transition:fly={{ x: 320, duration: 220 }}>
			<div class="help-head">
				<h3><Icon name="help-circle" size={17} /> ¿Cómo funciona el editor?</h3>
				<button class="help-close" onclick={() => helpOpen = false} aria-label="Cerrar">
					<Icon name="x" size={16} />
				</button>
			</div>
			<div class="help-body">
				<section>
					<h4>Las 3 áreas</h4>
					<ul class="help-list">
						<li><strong>Paleta</strong> (izquierda): elige el <em>trigger</em> y arrastra nodos al lienzo.</li>
						<li><strong>Lienzo</strong> (centro): conecta los nodos arrastrando desde el punto de salida hacia el siguiente.</li>
						<li><strong>Inspector</strong> (derecha): edita el nodo que selecciones.</li>
					</ul>
				</section>
				<section>
					<h4>Borrador vs Activo</h4>
					<p class="help-p">
						Un flujo en <strong>Borrador</strong> se puede editar y probar, pero no corre con
						clientes reales. Cuando esté listo, ponlo en <strong>Activo</strong> con el
						interruptor de arriba. Solo se activa si no tiene errores.
					</p>
				</section>
				<section>
					<h4>Tipos de paso</h4>
					<div class="help-nodes">
						{#each Object.entries(NODE_META) as [type, meta]}
							{#if NODE_HELP[type]}
								<div class="help-node">
									<span class="help-node-icon" style="color:{meta.color}"><Icon name={meta.icon} size={15} /></span>
									<div>
										<strong>{meta.label}</strong>
										<p>{NODE_HELP[type]}</p>
									</div>
								</div>
							{/if}
						{/each}
					</div>
				</section>
				<section>
					<h4>Variables</h4>
					<p class="help-p">
						Lo que el cliente responde en un paso <em>Pregunta</em> se guarda en una variable y
						lo puedes reutilizar escribiendo <code>{'{{nombre}}'}</code> en mensajes
						posteriores. También: <code>{'{{contact.name}}'}</code>, <code>{'{{contact.phone}}'}</code>.
					</p>
				</section>
			</div>
		</aside>
	{/if}
</div>

<style>
	.editor { position: fixed; inset: 0; background: var(--surface-1); z-index: 50; display: flex; flex-direction: column; }

	/* Banner de borrador */
	.draft-banner {
		display: flex; align-items: center; gap: 12px;
		padding: 11px 18px;
		background: var(--warning-soft);
		border-bottom: 1px solid var(--warning);
		color: var(--text-1);
		flex-shrink: 0;
	}
	.draft-banner > :global(svg) { color: var(--warning); flex-shrink: 0; }
	.draft-text { flex: 1; font-size: 12.5px; line-height: 1.45; }
	.draft-text strong { color: var(--text-1); }

	/* Panel "¿Cómo funciona?" */
	.help-scrim {
		position: fixed; inset: 0; z-index: 60;
		background: rgba(0,0,0,0.35); border: none; cursor: pointer;
	}
	.help-panel {
		position: fixed; top: 0; right: 0; bottom: 0; z-index: 61;
		width: 380px; max-width: 92vw;
		background: var(--surface-0);
		border-left: 1px solid var(--border-1);
		box-shadow: var(--shadow-xl);
		display: flex; flex-direction: column;
	}
	.help-head {
		display: flex; align-items: center; justify-content: space-between;
		padding: 16px 18px;
		border-bottom: 1px solid var(--border-1);
	}
	.help-head h3 { display: flex; align-items: center; gap: 8px; font-size: 15px; margin: 0; }
	.help-close {
		width: 30px; height: 30px; border-radius: 7px; color: var(--text-3);
		display: flex; align-items: center; justify-content: center;
	}
	.help-close:hover { background: var(--surface-2); color: var(--text-1); }
	.help-body { flex: 1; overflow-y: auto; padding: 18px; }
	.help-body section { margin-bottom: 22px; }
	.help-body h4 { font-size: 12px; text-transform: uppercase; letter-spacing: .04em; color: var(--text-3); margin: 0 0 9px; }
	.help-p { font-size: 13px; line-height: 1.55; color: var(--text-2); margin: 0; }
	.help-list { margin: 0; padding-left: 18px; font-size: 13px; line-height: 1.6; color: var(--text-2); }
	.help-p code, .help-list code {
		background: var(--surface-2); padding: 1px 5px; border-radius: 4px;
		font-size: 12px; color: var(--accent);
	}
	.help-nodes { display: flex; flex-direction: column; gap: 11px; }
	.help-node { display: flex; gap: 10px; align-items: flex-start; }
	.help-node-icon {
		width: 28px; height: 28px; flex-shrink: 0;
		display: flex; align-items: center; justify-content: center;
		background: var(--surface-2); border-radius: 7px;
	}
	.help-node strong { font-size: 12.5px; color: var(--text-1); }
	.help-node p { font-size: 12px; line-height: 1.4; color: var(--text-3); margin: 2px 0 0; }
	.head {
		height: 60px;
		display: flex; align-items: center; gap: 12px;
		padding: 0 18px;
		background: var(--surface-0);
		border-bottom: 1px solid var(--border-1);
		flex-shrink: 0;
	}
	.back-btn { width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; border-radius: 8px; color: var(--text-2); }
	.back-btn:hover { background: var(--surface-2); }
	.name-input {
		flex: 1;
		font-size: 16px;
		font-weight: 600;
		border: none !important;
		padding: 4px 0 !important;
		background: transparent !important;
	}
	.name-input:focus { box-shadow: none !important; }
	.active-toggle {
		display: flex !important; align-items: center; gap: 6px;
		font-size: 12px; font-weight: 600;
		color: var(--text-2);
		flex-direction: row !important;
		margin: 0 !important;
		cursor: pointer;
	}
	.active-toggle input { width: auto !important; margin: 0 !important; }
	.loading { flex: 1; display: flex; align-items: center; justify-content: center; }
	.spinner {
		width: 32px; height: 32px;
		border: 3px solid var(--border-1); border-top-color: var(--accent);
		border-radius: 50%; animation: spin 700ms linear infinite;
	}
	@keyframes spin { to { transform: rotate(360deg); } }

	.body {
		flex: 1;
		display: grid;
		grid-template-columns: 220px 1fr 320px;
		min-height: 0;
		overflow: hidden;
	}

	.palette {
		background: var(--surface-0);
		border-right: 1px solid var(--border-1);
		padding: 14px;
		overflow-y: auto;
		display: flex; flex-direction: column; gap: 6px;
	}
	.palette-title {
		font-size: 10.5px; font-weight: 700;
		text-transform: uppercase; letter-spacing: 0.06em;
		color: var(--text-3);
		margin-top: 12px; margin-bottom: 4px;
	}
	.palette-title:first-child { margin-top: 0; }
	.trigger-block { display: flex; flex-direction: column; gap: 6px; }
	.trigger-block input, .trigger-block select { font-size: 12.5px !important; padding: 7px 10px !important; }
	.trigger-help { font-size: 11.5px; color: #667085; margin: 0; line-height: 1.4; }
	.trigger-label { font-size: 11.5px; color: #475467; font-weight: 600; margin-top: 4px; }
	.channel-picker { display: flex; flex-direction: column; gap: 3px; }
	.channel-opt { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 400; }
	.channel-opt input { width: auto !important; padding: 0 !important; }
	.channel-empty { font-size: 11.5px; color: #98a2b3; }
	.priority-input { width: 64px !important; margin-left: 8px; }
	.inactivity-group { margin-top: 10px; padding-top: 10px; border-top: 1px solid var(--border, #e5e7eb); display: flex; flex-direction: column; gap: 6px; }
	.inactivity-title { font-size: 12px; font-weight: 600; color: var(--text-2); }
	.inactivity-row { display: flex; gap: 6px; }
	.inactivity-row input { width: 80px; }
	.inactivity-row select { flex: 1; }
	.bh-row { display: flex; gap: 8px; }
	.bh-row label { flex: 1; }
	.bh-days { display: flex; gap: 4px; margin-top: 4px; }
	.bh-day { width: 30px; height: 30px; border-radius: 6px; border: 1px solid var(--border, #e5e7eb); background: var(--surface-0, #fff); cursor: pointer; font-size: 12px; font-weight: 600; color: var(--text-2); }
	.bh-day.on { background: var(--accent, #4f6df5); color: #fff; border-color: var(--accent, #4f6df5); }
	.checkbox-label { display: flex; flex-direction: row !important; align-items: center; gap: 8px; font-size: 13px; }
	.checkbox-label input { width: auto !important; }

	.palette-list { display: flex; flex-direction: column; gap: 4px; }
	.palette-item {
		display: flex; align-items: center; gap: 8px;
		padding: 7px 10px;
		font-size: 12px;
		text-align: left;
		border-radius: 6px;
		color: var(--text-2);
		background: var(--surface-1);
		transition: background var(--transition-fast);
	}
	.palette-item:hover { background: var(--surface-2); color: var(--text-1); }
	.palette-item span:nth-child(2) { flex: 1; }
	.pi-icon {
		width: 22px; height: 22px;
		border-radius: 5px;
		display: flex; align-items: center; justify-content: center;
		flex-shrink: 0;
	}

	.test-block { display: flex; flex-direction: column; gap: 6px; }
	.test-block input { font-size: 12px !important; padding: 7px 10px !important; }
	.test-result {
		padding: 8px 10px;
		border-radius: 6px;
		background: var(--surface-1);
		font-size: 11.5px;
		max-height: 180px;
		overflow-y: auto;
	}
	.test-result.completed { background: var(--success-soft); }
	.test-result.error { background: var(--danger-soft); color: var(--danger); }
	.log-list { margin-top: 4px; color: var(--text-3); }
	.log-row { padding: 1px 0; font-family: var(--font-mono); font-size: 10.5px; }
	.test-result .err { color: var(--danger); }
	.log-id { color: var(--text-3); font-size: 9.5px; }

	.issues { display: flex; flex-direction: column; gap: 4px; }
	.issue {
		display: flex; align-items: flex-start; gap: 6px;
		padding: 6px 8px;
		border-radius: 6px;
		font-size: 11px;
		line-height: 1.4;
	}
	.issue.error { background: var(--danger-soft); color: var(--danger); }
	.issue.warn { background: var(--warning-soft); color: #92400e; }

	.stats-block {
		display: flex; flex-direction: column; gap: 4px;
		background: var(--surface-1);
		border-radius: 8px;
		padding: 10px;
		font-size: 12px;
	}
	.stat-row { display: flex; justify-content: space-between; }
	.stat-row span { color: var(--text-3); }
	.stat-subtitle { font-size: 10.5px; font-weight: 700; color: var(--text-3); margin-top: 6px; text-transform: uppercase; }
	.stat-drop {
		text-align: left;
		font-size: 11px;
		color: var(--text-2);
		padding: 3px 6px;
		border-radius: 4px;
	}
	.stat-drop:hover { background: var(--surface-2); color: var(--text-1); }

	.node-id { font-family: var(--font-mono); font-size: 10px; color: var(--text-3); }
	.icon-mini {
		width: 26px; height: 26px;
		display: flex; align-items: center; justify-content: center;
		border-radius: 6px;
		color: var(--text-3);
	}
	.icon-mini:hover { background: var(--surface-2); color: var(--text-1); }

	.canvas {
		overflow-y: auto;
		padding: 24px 32px;
		background:
			radial-gradient(circle at 20% 0%, rgba(79, 109, 245, 0.04), transparent 60%),
			var(--surface-1);
	}
	.canvas-empty {
		text-align: center; padding: 80px;
		color: var(--text-3);
		display: flex; flex-direction: column; align-items: center; gap: 8px;
	}

	/* ===== Graph canvas (n8n-lite) ===== */
	.canvas.graph { position: relative; padding: 0; overflow: auto; }
	.graph-toolbar {
		position: sticky; top: 0; left: 0; z-index: 5;
		display: flex; align-items: center; gap: 10px;
		padding: 10px 14px;
	}
	.conn-hint {
		font-size: 12px; font-weight: 600;
		background: var(--accent); color: #fff;
		padding: 4px 12px; border-radius: 9999px;
		box-shadow: var(--shadow-sm);
	}
	.graph-area { position: relative; }
	.edges { position: absolute; inset: 0; pointer-events: none; }
	.edge-label {
		font-size: 10px; font-weight: 700;
		fill: var(--text-3);
		paint-order: stroke; stroke: var(--surface-1); stroke-width: 3px;
	}
	.gnode {
		position: absolute;
		min-height: 70px;
		background: var(--surface-0);
		border: 1.5px solid var(--border-1);
		border-radius: 10px;
		padding: 10px 12px;
		cursor: grab;
		user-select: none;
		touch-action: none;
		box-shadow: var(--shadow-xs);
		transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
	}
	.gnode:active { cursor: grabbing; }
	.gnode:hover { border-color: var(--border-2); box-shadow: var(--shadow-sm); }
	.gnode.selected { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-soft); }
	.gnode.start { border-color: var(--accent); }
	.gnode.conn-target { outline: 2px dashed var(--accent); outline-offset: 3px; cursor: crosshair; }
	.gnode-row { display: flex; align-items: flex-start; gap: 10px; }
	.start-pill {
		position: absolute; top: -10px; left: 10px;
		display: inline-flex; align-items: center; gap: 3px;
		background: var(--accent); color: #fff;
		font-size: 9.5px; font-weight: 700;
		padding: 1px 8px; border-radius: 9999px;
	}
	.port {
		position: absolute;
		width: 14px; height: 14px;
		border-radius: 50%;
		border: 2px solid var(--accent);
		background: var(--surface-0);
		cursor: crosshair;
		z-index: 3;
		transition: transform var(--transition-fast), background var(--transition-fast);
	}
	.port.out { right: -8px; }
	.port.out:hover, .port.out.active { background: var(--accent); transform: scale(1.25); }
	.port.in {
		left: -8px;
		background: var(--accent-soft);
		animation: pulse 1s ease-in-out infinite;
	}
	@keyframes pulse { 50% { transform: scale(1.3); } }

	/* Menu options editor */
	.menu-options { display: flex; flex-direction: column; gap: 6px; }
	.menu-opt-row { display: grid; grid-template-columns: 18px 1fr 1fr 26px; gap: 6px; align-items: center; }
	.menu-opt-row input, .menu-opt-row select { font-size: 12px !important; padding: 6px 8px !important; }
	.opt-num { font-size: 11px; font-weight: 700; color: var(--text-3); text-align: center; }
	.nc-icon {
		width: 26px; height: 26px;
		border-radius: 6px;
		display: flex; align-items: center; justify-content: center;
		flex-shrink: 0;
	}
	.nc-text { flex: 1; min-width: 0; }
	.nc-type { font-size: 11px; font-weight: 700; color: var(--text-3); text-transform: uppercase; letter-spacing: 0.04em; }
	.nc-preview { font-size: 13px; color: var(--text-1); margin-top: 2px; line-height: 1.4; }
	.del-btn {
		width: 22px; height: 22px;
		display: flex; align-items: center; justify-content: center;
		border-radius: 4px;
		color: var(--text-3);
		opacity: 0;
		transition: opacity var(--transition-fast);
	}
	.gnode:hover .del-btn { opacity: 1; }
	.del-btn:hover { background: var(--danger-soft); color: var(--danger); }

	.inspector {
		background: var(--surface-0);
		border-left: 1px solid var(--border-1);
		padding: 16px;
		overflow-y: auto;
		display: flex; flex-direction: column; gap: 12px;
	}
	.inspector-empty { color: var(--text-3); font-size: 12.5px; text-align: center; padding: 60px 0; }
	.insp-head {
		display: flex; align-items: center; gap: 10px;
		padding-bottom: 10px;
		border-bottom: 1px solid var(--border-1);
	}
	.insp-head h4 { font-size: 14px; }
	.inspector label { font-size: 12.5px; }
	.inspector small { color: var(--text-3); font-size: 11px; }
	.conn-section {
		padding-top: 10px;
		border-top: 1px solid var(--border-1);
		display: flex; flex-direction: column; gap: 10px;
	}
	.insp-foot { padding-top: 8px; border-top: 1px solid var(--border-1); }
</style>
